import { createFileRoute } from "@tanstack/react-router";
import { Bell, CheckCheck, MoreHorizontal } from "lucide-react";
import PageHeader from "@/components/shared/PageHeader";
import EmptyState from "@/components/shared/EmptyState";
import { IconAction } from "@/components/shared/IconAction";
import { Button } from "@/components/ui/button";
import { useNotificationStore } from "@/store/notificationStore";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/student/notifications/")({
  component: StudentNotifications,
});

function StudentNotifications() {
  const { notifications, markAllRead, markAsRead } = useNotificationStore();
  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <div>
      <PageHeader
        title="Notifications"
        subtitle={`You have ${unreadCount} updates to review today.`}
        breadcrumbs={[{ label: "Dashboard", path: "/student/dashboard" }, { label: "Inbox" }]}
        action={
          <Button onClick={markAllRead}>
            <CheckCheck />
            Mark all read
          </Button>
        }
      />

      <div className="mx-auto max-w-3xl">
        {notifications.length === 0 ? (
          <EmptyState icon={<Bell />} title="Inbox is empty" description="New updates will show up here." />
        ) : (
          <ul className="divide-y divide-border overflow-hidden rounded-md border border-border bg-card">
            {notifications.map((n) => (
              <li
                key={n.id}
                className={cn(
                  "flex items-start gap-2 transition-colors hover:bg-muted",
                  !n.read && "bg-accent/40",
                )}
              >
                <button
                  type="button"
                  onClick={() => markAsRead(n.id)}
                  className="flex min-w-0 flex-1 gap-3 p-4 text-left focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none md:p-5"
                >
                  <span className="relative shrink-0">
                    <span
                      className="flex size-9 items-center justify-center rounded-sm border border-border bg-background text-body"
                      aria-hidden
                    >
                      <Bell className="size-4" />
                    </span>
                    {!n.read && (
                      <span
                        className="absolute -top-0.5 -right-0.5 size-2.5 rounded-full border-2 border-card bg-info"
                        aria-label="Unread"
                      />
                    )}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                      <span
                        className={cn(
                          "text-sm text-foreground",
                          n.read ? "font-normal" : "font-medium",
                        )}
                      >
                        {n.title}
                      </span>
                      <span className="text-caption text-muted-foreground">2 hours ago</span>
                    </span>
                    <span className="mt-0.5 block text-sm leading-relaxed text-muted-foreground">
                      {n.message}
                    </span>
                  </span>
                </button>
                <IconAction label="More options" icon={<MoreHorizontal />} className="mt-3 mr-3 md:mt-4 md:mr-4" />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default StudentNotifications;
